import { useDispatch, useSelector } from "react-redux";
import { login, logout, currentUser, acceptInvite } from "@/redux/reducers/AuthReducer";

/**
 * Custom hook to access authentication state and actions
 *
 * @returns {object} { isAuthenticated, login, logout, isLoading, isRefreshing, user }
 */
export const useAuth = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, isLoading, isRefreshing, user, authChecked } = useSelector((state) => state.auth);

  // Log in the user
  const handleLogin = async ({ mode, email, employeeId, password, acknowledged }) => {
    const resultAction = await dispatch(login({ mode, email, employeeId, password, acknowledged }));

    if (login.fulfilled.match(resultAction)) {
      return { success: true, data: resultAction.payload };
    } else {
      return {
        success: false,
        error: resultAction.payload || resultAction.error.message,
      };
    }
  };

  // Log in the user
  const handleAcceptInvite = async ({ token, password }) => {
    const resultAction = await dispatch(acceptInvite({ token, password }));

    if (acceptInvite.fulfilled.match(resultAction)) {
      return { success: true, data: resultAction.payload };
    } else {
      return {
        success: false,
        error: resultAction.payload || resultAction.error.message,
      };
    }
  };

  // Log out the user
  const handleLogout = async () => {
    const resultAction = await dispatch(logout());
    if (logout.fulfilled.match(resultAction)) {
      return { success: true };
    } else {
      return { success: false, error: resultAction.payload?.message || resultAction.error.message };
    }
  };

  // Fetch the current user data
  const fetchCurrentUser = () => {
    dispatch(currentUser());
  };

  return {
    isAuthenticated,
    login: handleLogin,
    acceptInvite: handleAcceptInvite,
    logout: handleLogout,
    currentUser: fetchCurrentUser,
    isLoading,
    // True only while a config reconciliation refetch is in flight. Never gate
    // a render on it — that is what `isLoading` is for, and gating on this one
    // would put the blank-page bug back.
    isRefreshing,
    user,
    authChecked,
  };
};
